import Spinner from "@/components/Spinner";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { FilterType } from "@/lib/filters";
import { FilterIcon } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FILTER_TYPE_ICONS } from "./FilterTypeOptions";

interface FilterTypeDropDownProps {
  filterType: string;
  onChange: (filterType: FilterType) => void;
  className?: string;
  showSpinner?: boolean;
  /** When provided, only these filter types are shown. */
  allowedFilterTypes?: FilterType[];
}

export default function FilterTypeDropDown({
  filterType,
  onChange,
  className,
  showSpinner,
  allowedFilterTypes,
}: FilterTypeDropDownProps) {
  const { t } = useTranslation();

  const allFilterOptions = useMemo(
    () => [
      {
        value: FilterType.IncludesString,
        label: t("table.filters.includesString"),
        icon: FILTER_TYPE_ICONS[FilterType.IncludesString],
      },
      {
        value: FilterType.ExcludesString,
        label: t("table.filters.excludesString"),
        icon: FILTER_TYPE_ICONS[FilterType.ExcludesString],
      },
      {
        separator: true,
      },
      {
        value: FilterType.EqualsString,
        label: t("table.filters.equalsString"),
        icon: FILTER_TYPE_ICONS[FilterType.EqualsString],
      },
      {
        value: FilterType.NotEquals,
        label: t("table.filters.notEquals"),
        icon: FILTER_TYPE_ICONS[FilterType.NotEquals],
      },
      {
        value: FilterType.LessThan,
        label: t("table.filters.lessThan"),
        icon: FILTER_TYPE_ICONS[FilterType.LessThan],
      },
      {
        value: FilterType.GreaterThan,
        label: t("table.filters.greaterThan"),
        icon: FILTER_TYPE_ICONS[FilterType.GreaterThan],
      },
      {
        value: FilterType.LessThanOrEqual,
        label: t("table.filters.lessThanOrEqual"),
        icon: FILTER_TYPE_ICONS[FilterType.LessThanOrEqual],
      },
      {
        value: FilterType.GreaterThanOrEqual,
        label: t("table.filters.greaterThanOrEqual"),
        icon: FILTER_TYPE_ICONS[FilterType.GreaterThanOrEqual],
      },
      {
        separator: true,
      },
      {
        value: FilterType.IsEmpty,
        label: t("table.filters.isEmpty"),
        icon: FILTER_TYPE_ICONS[FilterType.IsEmpty],
      },
      {
        value: FilterType.IsNotEmpty,
        label: t("table.filters.isNotEmpty"),
        icon: FILTER_TYPE_ICONS[FilterType.IsNotEmpty],
      },
    ],
    [t],
  );

  const filterOptions = useMemo(() => {
    if (!allowedFilterTypes?.length) {
      return allFilterOptions;
    }
    return allFilterOptions.filter(
      (opt) =>
        opt.separator ||
        (opt.value && allowedFilterTypes.includes(opt.value as FilterType)),
    );
  }, [allFilterOptions, allowedFilterTypes]);

  const CurrentFilterIcon =
    FILTER_TYPE_ICONS[filterType as FilterType] ?? FilterIcon;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className={className} asChild>
        <Button
          variant="ghost"
          size="icon"
          className="rounded-sm"
          data-testid="filter-type-dropdown-trigger"
        >
          {showSpinner ? (
            <Spinner className="w-4 h-4 ml-1" />
          ) : (
            <CurrentFilterIcon className="w-4 h-4" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuRadioGroup value={filterType} onValueChange={onChange}>
          {filterOptions.map((option, index) =>
            option.separator ? (
              <DropdownMenuSeparator key={index} />
            ) : (
              <DropdownMenuRadioItem
                key={option.value}
                value={option.value ?? ""}
              >
                <div className="flex items-center">
                  {option.icon && <option.icon className="mr-2 h-4 w-4" />}
                  {option.label}
                </div>
              </DropdownMenuRadioItem>
            ),
          )}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
